import { createSelector, createSlice } from '@reduxjs/toolkit'
import { FileHandleStore } from '~/utils/FileHandleStore'
import { FolderHasher } from '~/utils/folder-hasher'

// Define RootState type locally to avoid circular import with ~/store
// This matches the shape of the actual RootState but only includes what we need
interface DirHandleRootState {
  dirHandle: DirHandleState
}

// Callback for processing directory after adding a handle
// This is set by the app to avoid circular dependency with cronScheduler
type DirectoryProcessor = (
  dirHandle: FileSystemDirectoryHandle,
  id: string,
  force: boolean
) => Promise<void>

let _directoryProcessor: DirectoryProcessor | null = null

export const setDirectoryProcessor = (processor: DirectoryProcessor) => {
  _directoryProcessor = processor
}

// Store injection pattern to avoid circular dependency
// The store is injected after it's created via setDirHandleStore()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _store: { getState: () => any; dispatch: (action: any) => any } | null = null

export const setDirHandleStore = (store: typeof _store) => {
  _store = store
}

const getStore = () => {
  if (!_store) {
    throw new Error('Store not initialized. Call setDirHandleStore() first.')
  }
  return _store
}

interface SerializableHandle {
  id: string
  lastFolderHash?: string
  lastSyncedAt?: string // Timestamp of last successful sync
  name?: string // Store the handle name for easier reference
  securityStatus?: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING' // Track security status
}

interface UserDirHandleState {
  handles: SerializableHandle[]
  isInitialized: boolean
  permissionStatus: 'approved' | 'rejected' | 'prompt'
  error: string | null
  syncStatus?: 'syncing' | 'synced' | 'error' // Sync status
  lastSyncAt?: string // Timestamp of last sync attempt
}

interface DirHandleState {
  _currentUserEmail: string | null
  userStates: Record<string, UserDirHandleState>
  version: number
}

// This is used to check if the user state is up to date
export const CURRENT_DIRHANDLE_VERSION = 3

const createInitialUserState = (): UserDirHandleState => ({
  handles: [],
  isInitialized: false,
  error: null,
  permissionStatus: 'approved',
  syncStatus: undefined,
  lastSyncAt: undefined
})

const defaultState: DirHandleState = {
  _currentUserEmail: null,
  userStates: {},
  version: CURRENT_DIRHANDLE_VERSION
}

const fileHandleStore = new FileHandleStore()

// Type definitions for File System Access API permissions
interface FileSystemPermissionDescriptor {
  mode: 'read' | 'readwrite'
}

interface FileSystemHandleWithPermissions extends FileSystemDirectoryHandle {
  queryPermission(
    descriptor: FileSystemPermissionDescriptor
  ): Promise<'granted' | 'denied' | 'prompt'>
  requestPermission(
    descriptor: FileSystemPermissionDescriptor
  ): Promise<'granted' | 'denied' | 'prompt'>
}

export const checkPermission = async (dirHandle: FileSystemDirectoryHandle) => {
  const handleWithPermissions = dirHandle as FileSystemHandleWithPermissions
  const currentPermission = await handleWithPermissions.queryPermission({ mode: 'readwrite' })
  return currentPermission === 'granted'
}

export const requestPermission = async (dirHandle: FileSystemDirectoryHandle) => {
  const handleWithPermissions = dirHandle as FileSystemHandleWithPermissions
  const permission = await handleWithPermissions.requestPermission({ mode: 'readwrite' })
  return permission === 'granted'
}

// Async function to initialize user and handles (no Redux thunk)
export async function initializeUserAndHandles(userEmail: string): Promise<void> {
  // Initialize fileHandleStore
  await fileHandleStore.init()

  // Dispatch pending action
  getStore().dispatch(dirHandleSlice.actions.initializeUserPending(userEmail))

  try {
    // Get user's existing handles from state
    const currentState = getStore().getState() as DirHandleRootState
    const userState = currentState.dirHandle.userStates[userEmail] || createInitialUserState()
    const existingHandleIds = userState.handles.map(h => h.id)

    // Check permissions and verify handles
    const verifiedHandles: SerializableHandle[] = []
    let hasPrompt = false
    let hasRejected = false

    for (const handleId of existingHandleIds) {
      try {
        const storedHandle = await fileHandleStore.getHandleById(handleId)
        if (storedHandle) {
          const handleWithPermissions = storedHandle.handle as FileSystemHandleWithPermissions
          const permission = await handleWithPermissions.queryPermission({
            mode: 'readwrite'
          })

          // Store handle with name for easier reference
          const existingHandle = userState.handles.find(h => h.id === handleId)

          if (permission === 'granted') {
            verifiedHandles.push({
              id: handleId,
              lastFolderHash: existingHandle?.lastFolderHash,
              lastSyncedAt: existingHandle?.lastSyncedAt,
              name: storedHandle.handle.name
            })
          } else if (permission === 'prompt') {
            hasPrompt = true
            verifiedHandles.push({
              id: handleId,
              lastFolderHash: existingHandle?.lastFolderHash,
              lastSyncedAt: existingHandle?.lastSyncedAt,
              name: storedHandle.handle.name
            })
          } else if (permission === 'denied') {
            hasRejected = true
          }
        }
      } catch (error) {
        console.warn(`Failed to verify handle ${handleId}:`, error)
        hasRejected = true
      }
    }

    // Determine permission status
    let permissionStatus: 'approved' | 'rejected' | 'prompt'
    if (hasRejected) {
      permissionStatus = 'rejected'
    } else if (hasPrompt) {
      permissionStatus = 'prompt'
    } else {
      permissionStatus = 'approved'
    }

    // Version check
    const currentVersion = currentState.dirHandle.version ?? 0
    const shouldReset = !currentVersion || currentVersion < CURRENT_DIRHANDLE_VERSION

    // Get list of valid handles by name for S3 sync
    const verifiedHandleNames = verifiedHandles
      .map(handle => handle.name)
      .filter(Boolean) as string[]

    // Dispatch success action
    getStore().dispatch(
      dirHandleSlice.actions.initializeUserFulfilled({
        userEmail,
        handles: verifiedHandles,
        permissionStatus,
        shouldReset,
        validHandleNames: verifiedHandleNames
      })
    )
  } catch (error) {
    // Dispatch error action
    getStore().dispatch(
      dirHandleSlice.actions.initializeUserRejected({
        userEmail,
        error: error instanceof Error ? error.message : 'Failed to initialize user and handles'
      })
    )
    throw error
  }
}

// Async function to request all permissions (no Redux thunk)
export async function requestAllPermissions(): Promise<void> {
  const state = getStore().getState() as DirHandleRootState
  const userEmail = state.dirHandle._currentUserEmail
  if (!userEmail) throw new Error('No user initialized')

  const userState = state.dirHandle.userStates[userEmail]
  const existingHandleIds = userState.handles.map(h => h.id)

  const updatedHandles: SerializableHandle[] = []
  let hasRejected = false
  let hasPrompt = false

  for (const handleId of existingHandleIds) {
    try {
      const stored = await fileHandleStore.getHandleById(handleId)
      if (stored) {
        const handleWithPermissions = stored.handle as FileSystemHandleWithPermissions
        const currentPermission = await handleWithPermissions.queryPermission({
          mode: 'readwrite'
        })

        // Find existing handle to preserve properties
        const existingHandle = userState.handles.find(h => h.id === handleId)

        if (currentPermission === 'prompt') {
          const granted = await requestPermission(stored.handle)
          if (granted) {
            updatedHandles.push({
              id: handleId,
              lastFolderHash: existingHandle?.lastFolderHash,
              lastSyncedAt: existingHandle?.lastSyncedAt,
              name: stored.handle.name
            })
          } else {
            hasPrompt = true
          }
        } else if (currentPermission === 'granted') {
          updatedHandles.push({
            id: handleId,
            lastFolderHash: existingHandle?.lastFolderHash,
            lastSyncedAt: existingHandle?.lastSyncedAt,
            name: stored.handle.name
          })
        } else if (currentPermission === 'denied') {
          hasRejected = true
        }
      }
    } catch (error) {
      console.warn(`Failed to check permissions for handle ${handleId}:`, error)
      hasRejected = true
    }
  }

  // Determine new permission status
  let permissionStatus: 'approved' | 'rejected' | 'prompt'
  if (hasRejected) {
    permissionStatus = 'rejected'
  } else if (hasPrompt) {
    permissionStatus = 'prompt'
  } else {
    permissionStatus = 'approved'
  }

  // Dispatch action
  getStore().dispatch(
    dirHandleSlice.actions.updatePermissions({
      handles: updatedHandles,
      permissionStatus
    })
  )
}

// Helper function to check if a directory is empty (has no files)
async function isDirectoryEmpty(dirHandle: FileSystemDirectoryHandle): Promise<boolean> {
  const folderHasher = new FolderHasher()

  const checkForFiles = async (handle: FileSystemDirectoryHandle): Promise<boolean> => {
    const handleWithValues = handle as FileSystemDirectoryHandle & {
      values(): AsyncIterable<FileSystemHandle>
    }

    for await (const entry of handleWithValues.values()) {
      if (entry.kind === 'file') {
        // Check if this file should be included (not ignored)
        if (folderHasher.shouldIncludePath(entry.name)) {
          return false // Found a non-ignored file, directory is not empty
        }
      } else if (entry.kind === 'directory') {
        const entryPath = entry.name
        // Skip hidden directories and check if directory should be included
        if (!entryPath.startsWith('.') && folderHasher.shouldIncludePath(entryPath)) {
          const subDirHandle = entry as FileSystemDirectoryHandle
          const subDirEmpty = await checkForFiles(subDirHandle)
          if (!subDirEmpty) {
            return false // Found files in subdirectory
          }
        }
      }
    }
    return true // No files found
  }

  return await checkForFiles(dirHandle)
}

// Async function to add directory handle (no Redux thunk)
export async function addDirHandle(dirHandle: FileSystemDirectoryHandle): Promise<void> {
  const state = getStore().getState() as DirHandleRootState
  const userEmail = state.dirHandle._currentUserEmail
  if (!userEmail) throw new Error('No user initialized')

  const hasPermission = await checkPermission(dirHandle)
  if (!hasPermission) {
    const granted = await requestPermission(dirHandle)
    if (!granted) {
      throw new Error('Permission denied for directory')
    }
  }

  // Check if the directory is empty before adding it
  const isEmpty = await isDirectoryEmpty(dirHandle)
  if (isEmpty) {
    throw new Error('The selected folder is empty. Please select a folder that contains files.')
  }

  const id = await fileHandleStore.saveHandle(dirHandle)

  // Calculate the hash but don't wait for processing to complete
  // This lets us return quickly and update the UI
  try {
    const folderHasher = new FolderHasher()
    const folderHash = await folderHasher.getFolderHash(dirHandle)

    // Dispatch action to add the handle
    getStore().dispatch(
      dirHandleSlice.actions.addHandle({
        id,
        name: dirHandle.name,
        lastFolderHash: folderHash,
        securityStatus: 'SCANNING' // Mark as scanning initially
      })
    )

    // Process in the background without blocking
    setTimeout(async () => {
      try {
        // Upload the files using injected processor (avoids circular dependency with cronScheduler)
        if (_directoryProcessor) {
          await _directoryProcessor(dirHandle, id, true)
        } else {
          console.warn('Directory processor not set - skipping initial upload')
        }

        // Note: Sync will be triggered by WorkspaceSync component watching workspace changes
      } catch (error) {
        console.error('Failed to process directory in background:', error)

        // Check for virus detection in error message
        if (error instanceof Error && error.message.toLowerCase().includes('virus detected')) {
          updateSecurityStatus(id, 'THREATS_FOUND')
        }
      }
    }, 0)
  } catch (error) {
    console.error('Failed to calculate directory hash:', error)
    // Dispatch with basic info if hash calculation fails
    getStore().dispatch(
      dirHandleSlice.actions.addHandle({
        id,
        name: dirHandle.name
      })
    )
  }
}

// Async function to remove directory handle (no Redux thunk)
export async function removeDirHandle(id: string): Promise<void> {
  const state = getStore().getState() as DirHandleRootState
  const userEmail = state.dirHandle._currentUserEmail
  if (!userEmail) throw new Error('No user initialized')

  // Get the handle name before removing
  const userState = state.dirHandle.userStates[userEmail]
  const handleToRemove = userState.handles.find(h => h.id === id)
  const handleName = handleToRemove?.name

  await fileHandleStore.removeHandle(id)

  // Note: Deletion from S3 and sync will be handled by cronScheduler automatically on next cycle
  // which will detect the handle is no longer present and clean it up
  if (handleName) {
    console.info(`Removed handle ${handleName} - cronScheduler will sync changes`)
  }

  getStore().dispatch(dirHandleSlice.actions.removeHandle(id))
}

// Function to update security status (no Redux thunk)
export function updateSecurityStatus(
  id: string,
  securityStatus: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING'
): void {
  getStore().dispatch(dirHandleSlice.actions.setSecurityStatus({ id, securityStatus }))
}

// Function to update folder hash (no Redux thunk)
export function updateFolderHash(id: string, folderHash: string): void {
  const now = new Date().toISOString()
  getStore().dispatch(dirHandleSlice.actions.setFolderHash({ id, folderHash, lastSyncedAt: now }))
}

export const dirHandleSlice = createSlice({
  name: 'dirHandle',
  initialState: defaultState,
  reducers: {
    cleanupUserState: (state, action: { payload: string }) => {
      const userId = action.payload
      delete state.userStates[userId]
      if (state._currentUserEmail === userId) {
        state._currentUserEmail = null
      }
    },
    initializeUserPending: (state, action: { payload: string }) => {
      const userEmail = action.payload
      if (state.userStates == null) {
        state.userStates = {}
      }
      state._currentUserEmail = userEmail
      if (!state.userStates[userEmail]) {
        state.userStates[userEmail] = createInitialUserState()
      }
      state.userStates[userEmail].isInitialized = false
      state.userStates[userEmail].error = null
    },
    initializeUserFulfilled: (
      state,
      action: {
        payload: {
          userEmail: string
          handles: SerializableHandle[]
          shouldReset: boolean
          permissionStatus: 'approved' | 'rejected' | 'prompt'
          validHandleNames: string[]
        }
      }
    ) => {
      const { userEmail, handles, shouldReset, permissionStatus } = action.payload

      // Handle version migration
      if (shouldReset) {
        state.userStates = {}
        state.version = CURRENT_DIRHANDLE_VERSION
        state.userStates[userEmail] = createInitialUserState()
      }

      const userState = state.userStates[userEmail]
      userState.permissionStatus = permissionStatus

      const existingIds = new Set(userState.handles.map(h => h.id))
      const newIds = new Set(handles.map(h => h.id))

      userState.handles = userState.handles.filter(handle => newIds.has(handle.id))
      const handlesToAdd = handles.filter(handle => !existingIds.has(handle.id))
      userState.handles.push(...handlesToAdd)

      // Update existing handles with new properties
      handles.forEach(newHandle => {
        const existingHandle = userState.handles.find(h => h.id === newHandle.id)
        if (existingHandle && newHandle.name) {
          existingHandle.name = newHandle.name
        }
      })

      userState.isInitialized = true
      userState.error = null
      userState.syncStatus = 'synced'
      userState.lastSyncAt = new Date().toISOString()
    },
    initializeUserRejected: (state, action: { payload: { userEmail: string; error: string } }) => {
      const { userEmail, error } = action.payload
      const userState = state.userStates[userEmail]
      if (userState) {
        userState.error = error
        userState.isInitialized = false
        userState.syncStatus = 'error'
      }
    },
    addHandle: (
      state,
      action: {
        payload: {
          id: string
          name: string
          lastFolderHash?: string
          securityStatus?: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING'
        }
      }
    ) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]

      const newHandle = action.payload
      const existingHandleWithHash = userState.handles.find(handle => handle.id === newHandle.id)

      if (existingHandleWithHash) {
        return
      }

      // Add handle to state first so UI is updated
      userState.handles.push({
        id: newHandle.id,
        name: newHandle.name,
        lastFolderHash: newHandle.lastFolderHash,
        securityStatus: newHandle.securityStatus
      })
      userState.error = null
    },
    removeHandle: (state, action: { payload: string }) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      userState.handles = userState.handles.filter(handle => handle.id !== action.payload)
      userState.error = null
    },
    updatePermissions: (
      state,
      action: {
        payload: {
          handles: SerializableHandle[]
          permissionStatus: 'approved' | 'rejected' | 'prompt'
        }
      }
    ) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]

      const { handles, permissionStatus } = action.payload
      const existingIds = new Set(userState.handles.map(h => h.id))
      const newIds = new Set(handles.map(h => h.id))

      userState.handles = userState.handles.filter(handle => newIds.has(handle.id))
      const handlesToAdd = handles.filter(handle => !existingIds.has(handle.id))
      userState.handles.push(...handlesToAdd)

      // Update existing handles with names
      handles.forEach(newHandle => {
        const existingHandle = userState.handles.find(h => h.id === newHandle.id)
        if (existingHandle && newHandle.name) {
          existingHandle.name = newHandle.name
        }
      })

      userState.permissionStatus = permissionStatus
      userState.error = null
    },
    setFolderHash: (
      state,
      action: { payload: { id: string; folderHash: string; lastSyncedAt: string } }
    ) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      const handle = userState.handles.find(h => h.id === action.payload.id)
      if (handle) {
        handle.lastFolderHash = action.payload.folderHash
        handle.lastSyncedAt = action.payload.lastSyncedAt
      }
    },
    setSecurityStatus: (
      state,
      action: {
        payload: {
          id: string
          securityStatus: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING'
        }
      }
    ) => {
      if (!state._currentUserEmail) return
      const userState = state.userStates[state._currentUserEmail]
      const handle = userState.handles.find(h => h.id === action.payload.id)
      if (handle) {
        handle.securityStatus = action.payload.securityStatus
      }
    }
  }
})

// Selectors
export const selectCurrentUserEmail = (state: DirHandleRootState): string | null => {
  return state.dirHandle._currentUserEmail
}

export const selectUserDirHandleState = (
  state: DirHandleRootState,
  userEmail?: string
): UserDirHandleState => {
  const email = userEmail ?? state.dirHandle._currentUserEmail
  if (!email) {
    return {
      handles: [],
      isInitialized: false,
      permissionStatus: 'approved',
      error: null
    }
  }
  return (
    state.dirHandle.userStates[email] || {
      handles: [],
      isInitialized: false,
      error: null,
      permissionStatus: 'approved'
    }
  )
}

export const selectUserHandles = (
  state: DirHandleRootState,
  userEmail?: string
): SerializableHandle[] => {
  const userState = selectUserDirHandleState(state, userEmail)
  return userState.handles
}

export const selectUserHandleById = (
  state: DirHandleRootState,
  handleId: string,
  userEmail?: string
): SerializableHandle | undefined => {
  const handles = selectUserHandles(state, userEmail)
  return handles.find(handle => handle.id === handleId)
}

export const selectUserInitializationStatus = (
  state: DirHandleRootState,
  userEmail?: string
): { isInitialized: boolean; error: string | null; syncStatus?: string } => {
  const userState = selectUserDirHandleState(state, userEmail)
  return {
    isInitialized: userState.isInitialized,
    error: userState.error,
    syncStatus: userState.syncStatus
  }
}

export const selectHandleFolderHash = (
  state: DirHandleRootState,
  handleId: string,
  userEmail?: string
): string | undefined => {
  const handle = selectUserHandleById(state, handleId, userEmail)
  return handle?.lastFolderHash
}

export const selectAllHandlesWithHashes = createSelector(
  [selectUserHandles],
  (
    handles
  ): Array<{
    id: string
    lastFolderHash?: string
    name?: string
    securityStatus?: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING'
  }> =>
    handles.map(handle => ({
      id: handle.id,
      lastFolderHash: handle.lastFolderHash,
      name: handle.name,
      securityStatus: handle.securityStatus
    }))
)

export const { cleanupUserState } = dirHandleSlice.actions

export { defaultState as dirHandleDefaultState }
export default dirHandleSlice.reducer
